import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const AdminLeadSettings: React.FC = () => {
    const { token } = useAuth();
    
    const [showDeveloperLeads, setShowDeveloperLeads] = useState(false);
    const [showBrokerLeads, setShowBrokerLeads] = useState(false);
    const [showOwnerLeads, setShowOwnerLeads] = useState(false);
    const [adminUpiId, setAdminUpiId] = useState('');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const data = await api.getSystemSettings();
                if (data && !data.message) {
                    setShowDeveloperLeads(data.showDeveloperLeads || false);
                    setShowBrokerLeads(data.showBrokerLeads || false);
                    setShowOwnerLeads(data.showOwnerLeads || false);
                    setAdminUpiId(data.adminUpiId || '');
                }
            } catch (err) {
                console.error('Failed to fetch system settings:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, []);

    const handleSave = async () => {
        if (!token) return;
        setSaving(true);
        try {
            const data = await api.updateSystemSettings(token, {
                showDeveloperLeads,
                showBrokerLeads,
                showOwnerLeads,
                adminUpiId
            });
            if (data && !data.message) {
                toast.success('Lead visibility settings synchronized!');
            } else {
                toast.error(data.message || 'Transmission failure');
            }
        } catch (err) {
            console.error('Failed to update settings:', err);
            toast.error('An error occurred while saving protocol');
        } finally {
            setSaving(false);
        }
    };

    const ToggleCard = ({ title, desc, icon, state, setState, activeLabel, hiddenLabel }: any) => (
        <div className={`p-5 md:p-6 rounded-2xl border transition-all duration-300 ${state ? 'bg-blue-50/30 border-blue-200' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex-1">
                    <div className="flex items-center gap-4 mb-2">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg transition-all duration-300 ${state ? 'bg-[#05144b] text-white' : 'bg-slate-100 text-slate-400'}`}>
                            <i className={`fa-solid ${icon}`}></i>
                        </div>
                        <div>
                            <h4 className="text-lg font-bold text-black leading-none">{title}</h4>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1">{state ? 'Enabled' : 'Disabled'}</p>
                        </div>
                    </div>
                    <p className="text-sm font-normal text-slate-600 leading-normal max-w-md">
                        {desc}
                    </p>
                </div>
                
                <div className="flex flex-col items-center md:items-end gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${state ? 'text-[#05144b]' : 'text-slate-400'}`}>
                        {state ? activeLabel : hiddenLabel}
                    </span>
                    <button 
                        onClick={() => setState(!state)}
                        className={`relative inline-flex h-8 w-16 items-center rounded-full transition-all duration-300 focus:outline-none ${state ? 'bg-[#05144b]' : 'bg-slate-200'}`}
                    >
                        <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform duration-300 ${state ? 'translate-x-9' : 'translate-x-1'}`} />
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <AdminLayout>
            <div className="mx-auto max-w-5xl pb-20">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 border-b border-slate-100 pb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-black tracking-tight leading-none mb-3">
                            Lead Settings
                        </h1>
                        <p className="text-slate-600 text-sm font-medium">
                            Configure visibility permissions for professional roles.
                        </p>
                    </div>

                    <button 
                        onClick={handleSave}
                        disabled={saving}
                        className={`px-8 py-3.5 rounded-xl font-bold uppercase tracking-wider text-xs transition-all flex items-center gap-3 shadow-md active:scale-95 ${saving ? 'bg-slate-200 text-slate-400' : 'bg-[#05144b] text-white hover:bg-black'}`}
                    >
                        {saving ? (
                            <>
                                <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="fa-solid fa-save"></i>
                                Save Settings
                            </>
                        )}
                    </button>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-32 bg-white rounded-3xl border border-slate-50 shadow-sm">
                        <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-4"></div>
                        <p className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Loading Settings...</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-5">
                        <ToggleCard 
                            title="Developer Leads"
                            desc="Show or hide project leads in the Developer dashboard."
                            icon="fa-building-user"
                            state={showDeveloperLeads}
                            setState={setShowDeveloperLeads}
                            activeLabel="VISIBLE TO DEVELOPERS"
                            hiddenLabel="HIDDEN"
                        />
                        
                        <ToggleCard 
                            title="Broker Leads"
                            desc="Manage accessibility of the leads for brokerage partners."
                            icon="fa-users-viewfinder"
                            state={showBrokerLeads}
                            setState={setShowBrokerLeads}
                            activeLabel="VISIBLE TO BROKERS"
                            hiddenLabel="HIDDEN"
                        />

                        <ToggleCard 
                            title="Owner Leads"
                            desc="Allow owners to see direct inquiries for their listings."
                            icon="fa-user-tie"
                            state={showOwnerLeads}
                            setState={setShowOwnerLeads}
                            activeLabel="VISIBLE TO OWNERS"
                            hiddenLabel="HIDDEN"
                        />

                        {/* Admin UPI ID Setting */}
                        <div className="p-5 md:p-6 rounded-2xl border bg-white border-slate-200 shadow-sm">
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-[#05144b] text-white flex items-center justify-center text-lg">
                                        <i className="fa-solid fa-qrcode"></i>
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold text-black leading-none">Admin UPI ID</h4>
                                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1">For dynamic QR Code Generation</p>
                                    </div>
                                </div>
                                <p className="text-sm font-normal text-slate-600 leading-normal max-w-md">
                                    Enter the UPI ID where brokers will transfer the package payments.
                                </p>
                                <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                                    <input 
                                        type="text"
                                        value={adminUpiId}
                                        onChange={(e) => setAdminUpiId(e.target.value)}
                                        placeholder="example@upi"
                                        className="w-full md:max-w-md px-4 py-3 rounded-xl border-2 border-slate-100 focus:border-blue-600 focus:outline-none bg-slate-50 focus:bg-white text-sm font-semibold transition-all duration-300 text-slate-800"
                                    />
                                    <button
                                        onClick={handleSave}
                                        disabled={saving}
                                        className={`px-6 py-3 rounded-xl font-extrabold uppercase tracking-wider text-xs transition-all flex items-center justify-center gap-2 shadow-sm active:scale-95 border ${saving ? 'bg-slate-200 text-slate-400 border-slate-200' : 'bg-blue-600 border-blue-600 hover:bg-blue-700 text-white'}`}
                                    >
                                        {saving ? 'Saving...' : 'Save UPI ID'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 p-6 bg-slate-50 rounded-2xl border border-slate-100 flex items-start gap-4">
                            <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 text-slate-400 flex items-center justify-center shrink-0 shadow-sm">
                                <i className="fa-solid fa-info text-sm"></i>
                            </div>
                            <div>
                                <h5 className="text-xs font-bold text-black uppercase tracking-wider mb-1">Information</h5>
                                <p className="text-xs text-slate-500 font-medium leading-relaxed">
                                    Disabling lead visibility for a role will hide the menu item and dashboard cards for all users within that category. Administrators maintain full access.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default AdminLeadSettings;
